import React, { useEffect, useState } from 'react';
import { Button, Card, Row, Modal, Popconfirm, Table, notification } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import SubmissionForm from './SubmissionForm';
import { useLazyQuery, useMutation } from '@apollo/client';
import { DELETE_SUBMISSION, GET_SUBMISSIONS } from './SubmissionQueries';
import dayjs from 'dayjs';

const SubmissionList = () => {
  const isAuthenticated = useIsAuthenticated();
  const { proceedingId } = useParams<any>();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [participantId, setParticipantId] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);

  const handleParticipantFormSubmit = () => {
    refetch();
    setIsModalVisible(false);
    setParticipantId(null);
    setSubmissionId(null);
  };

  const handleModalOpen = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    refetch();
    setIsModalVisible(false);
    setParticipantId(null);
    setSubmissionId(null);
  };

  const getFormTitle = () => {
    if (isAuthenticated) {
      return (submissionId !== null ? 'Edit' : 'Add') + ' Submission Schedule';
    }
    return 'View Submission Schedule';
  };

  const [getSubmissions, { data: response, loading, error, refetch }] =
    useLazyQuery(GET_SUBMISSIONS);
  useEffect(() => {
    if (tab === '4' && proceedingId) {
      getSubmissions({
        variables: {
          input: proceedingId,
        },
      });
    }
  }, [tab]);
  const submissions = response?.submissions?.data;

  const [deleteSubmission, { loading: deleting, data: deleteResult, error: deleteError }] =
    useMutation(DELETE_SUBMISSION);
  const deleteSubmissionFromProceeding = async (submission: any) => {
    await deleteSubmission({
      variables: {
        input: {
          proceedingNumber: proceedingId,
          participantId: submission.participantId,
          submissionId: submission.id,
        },
      },
    });
  };
  useEffect(() => {
    if (deleteResult && deleteResult.deleteSubmission.result === 'Success') {
      notification.success({
        message: 'Success',
        description: `Submission has been deleted.`,
      });
    }

    if (deleteResult && deleteResult.deleteSubmission.result === 'Error') {
      notification.error({
        message: 'Error',
        description: `Error in deleting submission.`,
      });
    }
    refetch();
  }, [deleteResult, deleteError]);

  const editViewSubmission = (submission: any) => {
    setSubmissionId(submission.id);
    setParticipantId(submission.participantId);
    setIsModalVisible(true);
  };
  const columns: any[] = [
    {
      title: 'Submitter',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a: any, b: any) => a.displayName.localeCompare(b.displayName),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Deadline Date',
      dataIndex: 'submissionDeadlineDate',
      key: 'submissionDeadlineDate',
      sorter: (a: any, b: any) => {
        const dateA = a.submissionDeadlineDate ? dayjs(a.submissionDeadlineDate).unix() : 0;
        const dateB = b.submissionDeadlineDate ? dayjs(b.submissionDeadlineDate).unix() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Received Date',
      dataIndex: 'submissionReceivedDate',
      key: 'submissionReceivedDate',
    },
    {
      title: 'Status',
      dataIndex: 'submissionStatus',
      key: 'submissionStatus',
      render: (text: any) => (
        <span>
          {text}{' '}
          {text === 'Overdue' && (
            <ExclamationCircleOutlined style={{ color: 'red', marginLeft: 8 }} />
          )}
        </span>
      ),
    },
   
  ];
  if (isAuthenticated) {
    columns.push({
      title: 'Delete',
      key: 'deleteIcon',
      width: 50,
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      render: (_: any, submission: any) => {
        return (
          <>
            <div className="ml-1 inline-block">
              <Popconfirm
                title="Delete Submission"
                description={'Are you sure to delete this submission?'}
                onConfirm={() => deleteSubmissionFromProceeding(submission)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  icon={<DeleteOutlined />}
                  size={'small'}
                  style={{ backgroundColor: 'white', color: 'red' }}
                />
              </Popconfirm>
            </div>
          </>
        );
      },
    });
  }

  return (
    <>
      <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
        <Row>
          {isAuthenticated && (
            <Button type="primary" onClick={handleModalOpen} style={{ marginLeft: '10px' }}>
              Add Schedule
            </Button>
          )}
        </Row>
        <Row>
        <Card
  title={
    <div>
      <span className="highlighted-title">Submission Schedule</span>
      <p style={{ marginTop: '10px', fontWeight: 'normal' }}>
        &nbsp;&nbsp;&nbsp;The submission schedule is set by the hearing panel and outlines the dates each party is to submit information or evidence prior to the start of the hearing.
      </p>
    </div>
  }
  style={{ marginTop: '35px' }}
  className="card"
>
            <Table
              dataSource={submissions}
              loading={loading}
              columns={columns}
              onRow={(submission: any) => ({
                onClick: () => {
                  editViewSubmission(submission);
                },
                style: { cursor: 'pointer' },
              })}
              rowClassName="clickable-row"
            />
          </Card>
        </Row>
        <Modal
          title={getFormTitle()}
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={null}
          width={800}
          destroyOnClose
        >
          <SubmissionForm
            onSubmit={handleParticipantFormSubmit}
            onCancel={handleModalClose}
            proceedingId={proceedingId}
            participantId={participantId}
            submissionId={submissionId}
          />
        </Modal>
      </div>
    </>
  );
};

export default SubmissionList;
